import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutoComplete, Spin } from 'antd';
import { searchInstitutions } from '@/api/payees';
import type { FormValues, SchemaField } from '@/domain/schemaForm';

/**
 * DYNAMIC_SELECT 字段（典型：SWIFT code、美国 ACH 的 routing number）。
 *
 * 它是「可自由输入的搜索建议」，不是必须命中选项的下拉框：Airwallex 的机构搜索只用于
 * 帮用户确认银行，不能因为搜索库暂时没有某个合法代码就阻止提交。最终合法性仍由 schema
 * 正则与 /payees/validate 把关。
 */
export default function DynamicSelect({
  field,
  value,
  values,
  disabled,
  onChange,
}: {
  field: SchemaField;
  value: string | undefined;
  /** 整张表单的值：dynamic_options 的入参要从别的字段上取。 */
  values: FormValues;
  disabled?: boolean;
  onChange: (v: string) => void;
}) {
  const [options, setOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [loading, setLoading] = useState(false);
  /** 防抖 + 丢弃过期响应：慢的那一发回来时不能覆盖新的结果。 */
  const seq = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const keywordPattern = useMemo(() => {
    const p = field.field.dynamic_options?.query_params.find((q) => q.name === 'keyword')?.pattern;
    if (!p) return null;
    try {
      return new RegExp(p);
    } catch {
      return null;
    }
  }, [field]);

  const doSearch = useCallback(
    async (keyword: string) => {
      const mine = ++seq.current;
      // schema 给的 keyword pattern 通常是「至少 3 位字母数字」，不满足就别发请求
      if (keywordPattern && !keywordPattern.test(keyword)) {
        setOptions([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await searchInstitutions({
          keyword,
          bank_country_code: values['beneficiary.bank_details.bank_country_code'] ?? '',
          account_currency: values['beneficiary.bank_details.account_currency'] ?? '',
          transfer_method: values['transfer_method'] ?? '',
          // ⚠️ entity_type 是必传的，但 schema 的 query_params 里并没有列出来 ——
          // 不传会被 Airwallex 拒（validation_failed / source: entity_type）。
          entity_type: values['beneficiary.entity_type'] ?? '',
          local_clearing_system: values['beneficiary.bank_details.local_clearing_system'] || undefined,
        });
        if (mine === seq.current) setOptions(res.items);
      } catch {
        if (mine === seq.current) setOptions([]);
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    },
    [keywordPattern, values],
  );

  function onSearch(keyword: string) {
    if (timer.current) clearTimeout(timer.current);
    const normalized = keyword.trim().toUpperCase();
    timer.current = setTimeout(() => void doSearch(normalized), 300);
  }

  const dedupedOptions = useMemo(() => {
    const seen = new Set<string>();
    return options.flatMap((option) => {
      const code = option.value.trim().toUpperCase();
      if (!code || seen.has(code)) return [];
      seen.add(code);
      const bankName = option.label.trim();
      return [{
        value: code,
        // 银行名称可能相同，代码才是用户真正要提交、也是真正能区分分行的值。
        label: bankName && bankName !== code ? `${code} · ${bankName}` : code,
      }];
    });
  }, [options]);

  return (
    <AutoComplete
      allowClear
      disabled={disabled}
      value={value ?? ''}
      placeholder={field.field.placeholder || '输入代码或银行关键词'}
      // AutoComplete 保留自由输入；选项只作建议，不要求用户必须选中。
      filterOption={false}
      onSearch={onSearch}
      onChange={(next) => onChange((next ?? '').toUpperCase())}
      notFoundContent={loading ? <Spin size="small" /> : null}
      options={dedupedOptions}
    />
  );
}
